import { Navigate, Outlet, useSearchParams } from 'react-router-dom';

import { ArcusLoading } from '@/components/shared/ArcusLoading';
import { useSession } from '@/compositions/hooks/useSession';
import { resolveNextPath } from '@/utils/helpers/next-path';

/**
 * Guarda das portas de entrada: quem **já entrou** não vê tela de entrar.
 *
 * Cobre o login e a escolha do objetivo da conta — as duas telas que oferecem
 * "comece uma sessão" a quem já tem uma. Sem ela, abrir `/login` logado mostra
 * um formulário de e-mail e senha, e abrir `/register` logado pergunta "que tipo
 * de conta você gostaria de criar agora?" a quem já tem conta. Nenhuma das duas
 * é um beco — dá para sair pelo voltar —, mas as duas são a tela errada para
 * aquela pessoa.
 *
 * O destino é a intenção original (`?next=`) ou o mapa: se a pessoa chegou aqui
 * por um `?next=` é porque queria fazer alguma coisa, e ela **já pode**.
 *
 * ## ⚠️ Sessão anônima passa, de propósito
 *
 * `isAuthenticated` é `true` para a conta anônima do socorro sem cadastro
 * (DV-24) — para a RLS ela **é** uma sessão. Mas quem está nesse estado não tem
 * conta: mandá-la embora do login tiraria dela justamente a tela de que ela
 * precisa se já tiver uma conta de verdade e quiser entrar nela.
 *
 * Quem cuida da sessão anônima é a `RequireNotAnonymous`, e só no ramo do
 * cadastro — lá o destino certo é `/register/finish`, não o mapa. As duas
 * guardas se empilham no `/register` e cada uma responde a sua pergunta.
 *
 * ⚠️ Ela **não** cobre os formulários de cadastro (`/register/requester` e
 * irmãos): lá o destino de quem já tem sessão depende do objetivo escolhido —
 * voluntário vai para o passo de funções, institucional vai para a criação da
 * instituição —, e é a própria página que resolve, com `resolvePostSignUpPath`.
 */
export function RequireNoSession() {
  const { isAuthenticated, isAnonymous, isLoading } = useSession();
  const [searchParams] = useSearchParams();

  // Sem esperar a sessão, o primeiro quadro decide com ela ainda não lida e
  // deixa passar quem deveria ser redirecionada — o mesmo cuidado das outras
  // guardas do módulo.
  if (isLoading) return <ArcusLoading className="h-dvh" />;

  if (isAuthenticated && !isAnonymous) {
    return <Navigate to={resolveNextPath(searchParams.get('next'))} replace />;
  }

  return <Outlet />;
}
